import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import RateshopperLayout from '@app/site/secured/rateshopper/layout/RateshopperLayout';
import RateshopperModulesRouter from '@app/site/secured/rateshopper/RateshopperModulesRouter';
import ErrorPageNotFound from '@app/site/system/page/ErrorPageNotFound';
import Domain from '@app/model/_common/Domain';
import useSecurity from '@util/SecurityHook';
import ReviewManagerRouter from '@app/site/secured/review-manager/ReviewManagerRouter';
import ReviewManagerLayout from '@app/site/secured/review-manager/_layout/ReviewManagerLayout';
import RmsLayout from '@app/site/secured/rms/RmsLayout';
import RmsDashboardPage from '@app/site/secured/rms/RmsDashboardPage';
import BiLayout, { BiIndexRedirect } from '@app/site/secured/bi/BiLayout';
import { useBiAccess } from '@app/site/secured/bi/BiScopeHooks';
import BiDashboardPage from '@app/site/secured/bi/page/BiDashboardPage';
import BiSegmentationPage from '@app/site/secured/bi/page/BiSegmentationPage';
import BiRoomTypesPage from '@app/site/secured/bi/page/BiRoomTypesPage';
import BiRatePlansPage from '@app/site/secured/bi/page/BiRatePlansPage';
import BiGuestMixPage from '@app/site/secured/bi/page/BiGuestMixPage';
import BiChannelsPage from '@app/site/secured/bi/page/BiChannelsPage';
import BiDailyPerformancePage from '@app/site/secured/bi/page/BiDailyPerformancePage';
import BiPickupMonitoringPage from '@app/site/secured/bi/page/BiPickupMonitoringPage';
import BiSegmentationComparePage from '@app/site/secured/bi/page/BiSegmentationComparePage';
import GeneralSettingsPage from '@app/site/secured/general-settings/page/GeneralSettingsPage';
import GeneralSettingsFragment from '@app/site/secured/general-settings/fragment/general/GeneralSettingsFragment';
import PropertyAccessSettingsPage
	from '@app/site/secured/general-settings/fragment/property-access/PropertyAccessSettingsPage';
import CompanyDepartmentsSettingsPage
	from '@app/site/secured/general-settings/fragment/company-departments/CompanyDepartmentsSettingsPage';
import IntegrationsSettingsPage from '@app/site/secured/general-settings/fragment/integrations/IntegrationsSettingsPage';
import IntegrationsPropertyPage from '@app/site/secured/general-settings/fragment/integrations/IntegrationsPropertyPage';
import GeneralSettingsLayout from '@app/site/secured/general-settings/layout/GeneralSettingsLayout';
import useDomainTransition from '@app/hook/useDomainTransition';
import DomainTransitionLoader from '@app/component/loader/DomainTransitionLoader';
import { DomainTransitionProvider } from '@app/context/DomainTransitionContext';
import PageLoaderOverlay from '@app/site/PageLoaderOverlay';
import CompanyHotelsProvider from '@app/site/secured/rateshopper/rates/provider/CompanyHotelsProvider';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import LatestProcessRequestExecutionStateMonitorProvider
	from '@app/site/secured/rateshopper/rates/provider/LatestProcessRequestExecutionStateMonitorProvider';
import MetaradarRefreshWatchProvider from '@app/site/secured/rateshopper/rates/provider/MetaradarRefreshWatchProvider';

const DomainRouterContent = React.memo(() => {
	// The Metaradar watch stays above CompanyHotelsProvider: a hotel change must not end it (ROO2-232).
	return <MetaradarRefreshWatchProvider>
		<CompanyHotelsProvider loadingPlaceholder={<PageLoaderOverlay />}>
			<LatestProcessRequestExecutionStateMonitorProvider>
				<DomainRouterContentInternal />
			</LatestProcessRequestExecutionStateMonitorProvider>
		</CompanyHotelsProvider>
	</MetaradarRefreshWatchProvider>;
});

const DomainRouterContentInternal = React.memo(() => {
	// check domains
	const { isTransitioning } = useDomainTransition();
	const security = useSecurity();
	const { companyHotel } = useSelectedCompanyHotel();

	const hasRateshopperEnabled = security.hasCompanyHotelDomainAuthority(companyHotel, Domain.RATESHOPPER) && companyHotel.hasRateShopperDomain();
	const hasReviewManagerEnabled = security.hasCompanyHotelDomainAuthority(companyHotel, Domain.REVIEW_MANAGER) && companyHotel.hasReviewManagerDomain();
	const hasRmsEnabled = security.hasRmsAccess(companyHotel) && companyHotel.hasRmsDomain();
	// ROO2-274: BI with a BI right on any property. While the BI scope is asked, a BI address waits instead of "not found".
	const biAccess = useBiAccess();
	const hasBiToolEnabled = biAccess.state === 'yes';
	const biLoading = biAccess.state === 'loading';

	return <>
		<DomainTransitionLoader isVisible={isTransitioning} />
		<Routes>
			{hasRateshopperEnabled && <>
				<Route path="/dashboard" element={<Navigate to="/secured/rateshopper/dashboard" />} />
				<Route path="rateshopper" element={<RateshopperLayout />}>
					<Route path="*" element={<RateshopperModulesRouter />}>
					</Route>
				</Route>
			</>}
			{hasReviewManagerEnabled && <>
				{!hasRateshopperEnabled &&
					<Route path="/dashboard" element={<Navigate to="/secured/review-manager/dashboard" />} />}
				<Route path="review-manager" element={<ReviewManagerLayout />}>
					<Route path="*" element={<ReviewManagerRouter />}>
					</Route>
				</Route>
			</>}
			{hasRmsEnabled && <>
				{!hasRateshopperEnabled && !hasReviewManagerEnabled &&
					<Route path="/dashboard" element={<Navigate to="/secured/rms/dashboard" />} />}
				<Route path="rms" element={<RmsLayout />}>
					<Route path="dashboard" element={<RmsDashboardPage />} />
					<Route index element={<Navigate to="dashboard" replace />} />
				</Route>
			</>}
			{hasBiToolEnabled && <>
				{!hasRateshopperEnabled && !hasReviewManagerEnabled && !hasRmsEnabled &&
					<Route path="/dashboard" element={<Navigate to="/secured/bi/dashboard" />} />}
				<Route path="bi" element={<BiLayout />}>
					<Route index element={<BiIndexRedirect />} />
					<Route path="dashboard" element={<BiDashboardPage />} />
					<Route path="segmentation" element={<BiSegmentationPage />} />
					<Route path="room-types" element={<BiRoomTypesPage />} />
					<Route path="rate-plans" element={<BiRatePlansPage />} />
					<Route path="guest-mix" element={<BiGuestMixPage />} />
					<Route path="channels" element={<BiChannelsPage />} />
					<Route path="daily-performance" element={<BiDailyPerformancePage />} />
					<Route path="pickup-monitoring" element={<BiPickupMonitoringPage />} />
					<Route path="compare/segmentation" element={<BiSegmentationComparePage />} />
				</Route>
			</>}
			{biLoading && <>
				{!hasRateshopperEnabled && !hasReviewManagerEnabled && !hasRmsEnabled &&
					<Route path="/dashboard" element={<PageLoaderOverlay />} />}
				<Route path="bi/*" element={<PageLoaderOverlay />} />
			</>}
			<Route element={<GeneralSettingsLayout />}>
				<Route path="settings" element={<GeneralSettingsPage />}>
					<Route path="general" element={<GeneralSettingsFragment />} />
					<Route path="property-access" element={<PropertyAccessSettingsPage />} />
					<Route path="company-departments" element={<CompanyDepartmentsSettingsPage />} />
					<Route path="integrations" element={<IntegrationsSettingsPage />} />
					<Route path="integrations/:companyHotelId" element={<IntegrationsPropertyPage />} />
				</Route>
			</Route>
			<Route path="*" element={<ErrorPageNotFound />} errorElement={<ErrorPageNotFound />} />
		</Routes>
	</>;
});

const DomainRouter = React.memo(() => {
	return (
		<DomainTransitionProvider>
			<DomainRouterContent />
		</DomainTransitionProvider>
	);
});

export default DomainRouter;
